import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, timeAgo, severityStyle } from "@/components/shared";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  ShieldAlert,
  ScanSearch,
  Bug,
  Activity,
  Layers,
  Loader2,
  ChevronRight,
} from "lucide-react";

function ScoreRing({ score }) {
  const r = 70;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const offset = c - (pct / 100) * c;
  const color = pct >= 70 ? "#00FF41" : pct >= 40 ? "#FFD700" : "#FF003C";
  return (
    <div className="relative w-48 h-48">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
        <circle cx="80" cy="80" r={r} fill="none" stroke="#1A2E1E" strokeWidth="8" />
        <circle
          cx="80" cy="80" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="butt"
          style={{ transition: "stroke-dashoffset 1s ease, stroke 0.3s ease", filter: `drop-shadow(0 0 8px ${color}99)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-mono font-bold text-5xl" style={{ color }}>{pct}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Security Score</div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = "text-primary", delay = 0, testid }) {
  return (
    <div data-testid={testid} className="bg-card border border-border p-5 animate-fade-up" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center justify-between mb-4">
        <Icon size={20} strokeWidth={1.5} className={accent} />
      </div>
      <div className={`font-mono font-bold text-3xl ${accent}`}>{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get("/dashboard").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" strokeWidth={1.5} />
      </div>
    );

  const protectedNow = data.status === "protected";

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <PageHeader
        tag={`Welcome back, ${user?.name?.split(" ")[0] || "operator"}`}
        title="Security Dashboard"
        subtitle="Live overview of your protection status, threats and system defense grid."
        right={
          <Button
            data-testid="dashboard-scan-btn"
            onClick={() => navigate("/app/scanner")}
            className="rounded-none font-mono bg-primary text-primary-foreground hover:bg-primary/90 glow-green"
          >
            <ScanSearch size={16} strokeWidth={1.5} className="mr-2" /> Run scan
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Score + status */}
        <div className="lg:col-span-1 bg-card border border-border p-8 flex flex-col items-center justify-center text-center animate-fade-up">
          <ScoreRing score={data.security_score} />
          <div
            data-testid="protection-status"
            className={`mt-6 inline-flex items-center gap-2 font-mono text-sm px-4 py-2 border ${
              protectedNow ? "text-primary border-primary/50 bg-primary/10" : "text-destructive border-destructive/50 bg-destructive/10"
            }`}
          >
            {protectedNow ? <ShieldCheck size={16} strokeWidth={1.5} /> : <ShieldAlert size={16} strokeWidth={1.5} />}
            {protectedNow ? "SYSTEM PROTECTED" : "ACTION REQUIRED"}
          </div>
        </div>

        {/* Stat grid */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <StatCard testid="stat-threats-blocked" icon={ShieldCheck} label="Threats blocked" value={data.threats_blocked.toLocaleString()} delay={60} />
          <StatCard testid="stat-active-threats" icon={Bug} label="Active threats" value={data.active_threats} accent={data.active_threats > 0 ? "text-destructive" : "text-primary"} delay={120} />
          <StatCard testid="stat-total-scans" icon={Activity} label="Scans run" value={data.total_scans} delay={180} />
          <StatCard testid="stat-modules" icon={Layers} label="Modules active" value={`${data.active_modules}/${data.total_modules}`} delay={240} />
        </div>
      </div>

      {/* Recent scans */}
      <div className="mt-4 bg-card border border-border animate-fade-up" style={{ animationDelay: "260ms" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="font-mono font-bold tracking-tight text-lg">Recent Scans</h3>
          <span className="font-mono text-xs text-muted-foreground">last scan: {timeAgo(data.last_scan)}</span>
        </div>
        {data.recent_scans.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground font-mono text-sm">
            No scans yet. Run your first scan to populate this feed.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.recent_scans.map((s, i) => (
              <div key={i} className="flex items-center justify-between px-6 py-4 hover:bg-primary/[0.03] transition-colors">
                <div className="flex items-center gap-4">
                  <div className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-widest ${severityStyle(s.threats_found > 0 ? "critical" : "success")}`}>
                    {s.scan_type}
                  </div>
                  <div>
                    <div className="font-mono text-sm">{s.target}</div>
                    <div className="text-xs text-muted-foreground">{timeAgo(s.timestamp)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className={`font-mono text-sm ${s.threats_found > 0 ? "text-destructive" : "text-primary"}`}>
                      {s.threats_found} threat{s.threats_found === 1 ? "" : "s"}
                    </div>
                    <div className="text-xs text-muted-foreground">score {s.score}</div>
                  </div>
                  <ChevronRight size={16} strokeWidth={1.5} className="text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
